import clsx, { ClassValue } from "clsx";
import type { CSSProperties, JSX } from "react";

import "./styles.css";

export type SkeletonKind =
  | "card"
  | "tile"
  | "title"
  | "subtitle"
  | "paragraph"
  | "button"
  | "buttonText"
  | "input"
  | "inputWithoutLabel"
  | "header"
  | "image"
  | "avatar"
  | "editor"
  | "absolute"
  | "pagination"
  | "colorOptions"
  | "icon"
  | "tag";

type Props = Readonly<{
  kind?: SkeletonKind;
  className?: ClassValue;
  amount?: number;
  style?: CSSProperties;
}>;

const colorOptions = ["#1D4D9F", "#440D62", "#1B1C32", "#EA3E00", "#430D62", "#FFD2C8"];

export const LoadingSkeleton = ({ kind, className, amount = 1, style }: Props): JSX.Element => {
  const classes = clsx({
    "h-40 w-40": kind === "card",
    "h-40 sm:h-32 w-full": kind === "tile",
    "h-5 w-full": kind === "title",
    "h-8 min-h-8 w-full": kind === "subtitle",
    "h-3 w-full": kind === "paragraph",
    "min-h-button max-w-button w-full": kind === "button",
    "max-w-40 h-5 w-full": kind === "buttonText",
    "h-5 w-full rounded": kind === "input",
    "h-12 w-full": kind === "inputWithoutLabel",
    "h-24 w-full": kind === "header",
    "h-20 w-20": kind === "avatar",
    "h-96 w-full": kind === "editor",
    "absolute -inset-[1px] z-[11] h-[calc(100%+2px)] w-[calc(100%+2px)]": kind === "absolute",
    "h-3 w-full max-w-[13.5rem]": kind === "pagination",
    "h-6 w-6": kind === "icon",
    "h-4 w-14 rounded-full": kind === "tag",
    "h-full w-full": !kind || kind === "image",
  });

  if (kind === "colorOptions") {
    return (
      <div className={clsx("loading-skeleton-color-options flex", className)} style={style}>
        {colorOptions.map((color, index) => (
          <div
            key={index}
            className={clsx(
              "m-[2px] h-[1.25rem] w-[1.25rem] rounded-full outline outline-2 outline-base-white blur-sm",
              { "ml-[-.5rem]": index },
            )}
            style={{
              background: color,
            }}
          />
        ))}

        <div className="extras-counter ml-2 h-3 w-[1.75rem]" />
      </div>
    );
  }

  return (
    <>
      {Array.from({ length: amount ?? 1 }).map((_, index) => (
        <div
          key={index}
          className={clsx("loading-skeleton shine", classes, className, {
            "is-image": kind === "image",
          })}
          style={style}
        />
      ))}
    </>
  );
};
